import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import DialogContent from '@mui/material/DialogContent';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { SparkleIcon } from '@openx/components-core/icons/SparkleIcon/SparkleIcon.tsx';
import { WarningIcon } from '@phosphor-icons/react/Warning';
import { WarningCircleIcon } from '@phosphor-icons/react/WarningCircle';
import { useTranslation } from 'react-i18next';
import type { PackageEditConfirmationMode } from '../useEditConfirmationModal.tsx';

export type EditConfirmationModalContentProps = {
  deals: { name: string; action: string }[];
  hasMore?: boolean;
  mode?: PackageEditConfirmationMode;
  isRBODynamic?: boolean;
};

export const EditConfirmationModalContent = ({
  deals,
  hasMore,
  mode = 'default',
  isRBODynamic,
}: EditConfirmationModalContentProps) => {
  const theme = useTheme();
  const { t } = useTranslation();

  const dealPluralityLabel = deals.length > 1 ? t('deals') : t('deal');
  const isDisableRbo = mode === 'disable_rbo';
  const isRemoveRetargeting = mode === 'remove_retargeting';
  const showResultsByOpenXHeader = isDisableRbo || isRBODynamic;

  return (
    <DialogContent dividers sx={{ pt: 1 }}>
      {showResultsByOpenXHeader && (
        <Typography
          variant="h3"
          gutterBottom
          data-test="heading"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {t('Engagement Optimization')}
          <SparkleIcon />
        </Typography>
      )}
      {isDisableRbo && (
        <Alert severity="error" sx={{ marginBottom: 2 }} data-test="incompatible-targeting-alert">
          {t(
            'Incompatible Targeting Detected. Engagement optimization is only supported for Banner or Video inventory. Saving these changes will disable the feature for the following deals. '
          )}
          <strong>{t('Steady state performance may take up to 48 hours.')}</strong>
        </Alert>
      )}
      {isRemoveRetargeting && (
        <Alert severity="error" sx={{ marginBottom: 2 }} data-test="retargeting-incompatible-alert">
          {t(
            'Incompatible DSP Detected. Retargeting requires an Aqua DSP on associated deals. Saving these changes will remove Retargeting from this package.'
          )}
        </Alert>
      )}
      {isRBODynamic && (
        <Alert severity="warning" sx={{ marginBottom: 2 }} data-test="rbo-alert">
          <strong>{t('CAUTION: ')}</strong>
          {t(
            'Changing deal/package targeting will cause this deal to run without Engagement optimization while request volumes are reassessed. '
          )}
          <strong>{t('Steady state performance may take up to 48 hours.')}</strong>
        </Alert>
      )}
      {deals.length > 0 ? (
        <>
          <Typography variant="h3" gutterBottom data-test="intro-message">
            {t('This Package is used by following {dealPluralityLabel}:', { dealPluralityLabel })}
          </Typography>
          <Box sx={{ maxHeight: '225px', overflowY: 'auto' }} data-test="deals-container">
            {deals.map(({ name, action }) => {
              return (
                <Box key={name} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body1Bold" data-test={'deal-name'}>
                    {name}
                  </Typography>
                  {action === 'disable_on_incompatible' && isDisableRbo && (
                    <WarningCircleIcon color={theme.palette.error.main} size={20} />
                  )}
                  {action === 'remove_retargeting' && isRemoveRetargeting && (
                    <WarningCircleIcon color={theme.palette.error.main} size={20} />
                  )}
                  {action === 'reset' && isRBODynamic && <WarningIcon color={theme.palette.warning.main} size={20} />}
                </Box>
              );
            })}
            {hasMore && (
              <Typography variant="body1Bold" data-test={'and-more'}>
                {t('and more')}
              </Typography>
            )}
          </Box>
        </>
      ) : (
        <Typography variant="body1Bold" data-test={'package-hidden-deals'}>
          {t('The package is used by some deals')}
        </Typography>
      )}
      <Typography
        variant="body1"
        data-test="explanation"
        sx={{
          mt: 1,
        }}
      >
        {t(
          "If updated, changes will affect all associated deals. If you would like to modify this package's targeting without impacting associated deals, you can clone the package and use it with another deal. Press save to update the package and confirm updates."
        )}
      </Typography>
    </DialogContent>
  );
};
